import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import StickyQuoteButton from "@/components/feature/StickyQuoteButton";
import { useSEO } from "@/hooks/useSEO";

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "ExaGrid Backup Malaysia | Ransomware-Proof Backup Appliance | SE Distribution",
  "description": "ExaGrid backup appliances in Malaysia — ransomware-proof with non-network-facing repository, instant VM recovery, 10–50x deduplication. Authorized distributor in Kuala Lumpur.",
  "url": "https://www.sedistribution.com.my/exagrid-backup-malaysia",
  "publisher": {
    "@type": "Organization",
    "name": "SE Distribution Sdn Bhd",
    "url": "https://www.sedistribution.com.my"
  }
};

const productSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "ExaGrid Backup Appliances — Malaysia",
  "url": "https://www.sedistribution.com.my/exagrid-backup-malaysia",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "item": {
        "@type": "Product",
        "name": "ExaGrid EX84 Backup Appliance",
        "description": "Flagship 4U backup appliance — 84TB full backup per node, 24×8TB drives, 128 GiB RAM, non-network-facing ransomware protection, instant VM recovery from landing zone. Up to 32 appliances per system for 2.7PB.",
        "brand": { "@type": "Brand", "name": "ExaGrid" },
        "sku": "EX84",
        "url": "https://www.sedistribution.com.my/exagrid-backup-malaysia",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Full Backup Capacity", "value": "84TB" },
          { "@type": "PropertyValue", "name": "Form Factor", "value": "4U" },
          { "@type": "PropertyValue", "name": "Memory", "value": "128 GiB" },
          { "@type": "PropertyValue", "name": "Disks", "value": "24 × 8TB Enterprise SAS" },
          { "@type": "PropertyValue", "name": "Ransomware Protection", "value": "Non-network-facing repository + immutable objects + retention time-lock" }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "MYR",
          "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "38",
          "bestRating": "5"
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 2,
      "item": {
        "@type": "Product",
        "name": "ExaGrid EX36 Backup Appliance",
        "description": "Mid-range 2U backup appliance — 36TB full backup, 12×8TB drives, 64 GiB RAM, 12 hyper-threaded cores, adaptive deduplication (10:1–50:1), RAID6 protection.",
        "brand": { "@type": "Brand", "name": "ExaGrid" },
        "sku": "EX36",
        "url": "https://www.sedistribution.com.my/exagrid-backup-malaysia",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Full Backup Capacity", "value": "36TB" },
          { "@type": "PropertyValue", "name": "Form Factor", "value": "2U" },
          { "@type": "PropertyValue", "name": "Memory", "value": "64 GiB" },
          { "@type": "PropertyValue", "name": "Disks", "value": "12 × 8TB Enterprise SAS" }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "MYR",
          "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "27",
          "bestRating": "5"
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 3,
      "item": {
        "@type": "Product",
        "name": "ExaGrid EX10 Backup Appliance",
        "description": "Entry-level 2U backup appliance — 10TB full backup, 8×4TB drives, 16 GiB RAM, 4-core CPU. Scales up to any ExaGrid model — mix and match in a single system.",
        "brand": { "@type": "Brand", "name": "ExaGrid" },
        "sku": "EX10",
        "url": "https://www.sedistribution.com.my/exagrid-backup-malaysia",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Full Backup Capacity", "value": "10TB" },
          { "@type": "PropertyValue", "name": "Form Factor", "value": "2U" },
          { "@type": "PropertyValue", "name": "Memory", "value": "16 GiB" },
          { "@type": "PropertyValue", "name": "Disks", "value": "8 × 4TB Enterprise SAS" }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "MYR",
          "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" }
        }
      }
    }
  ]
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What makes ExaGrid ransomware-proof?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "ExaGrid uses a non-network-facing repository — the Tiered Air Gap — where backup data is stored in a separate zone that is not accessible from the network. Combined with immutable objects and retention time-lock, ransomware cannot encrypt, delete, or modify backup data even if the production environment is fully compromised."
      }
    },
    {
      "@type": "Question",
      "name": "How fast is ExaGrid VM recovery?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "ExaGrid's landing zone keeps the most recent backups in full, undeduplicated form — meaning VMs can be recovered instantly without waiting for rehydration. Most organizations achieve sub-15 minute VM recovery times, compared to hours with traditional deduplication appliances."
      }
    },
    {
      "@type": "Question",
      "name": "Does ExaGrid work with Veeam?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. ExaGrid integrates natively with Veeam Backup & Replication as a backup storage target. It also supports over 25 backup applications including Commvault, Veritas NetBackup, Veritas Backup Exec, IBM Spectrum Protect (TSM), Dell EMC NetWorker, Arcserve Backup/D2D/UDP, Micro Focus Data Protector, Quest NetVault, StorageCraft ShadowProtect, Idera SQL Safe, Oracle RMAN, Zerto Offsite Backup, HYCU, and BridgeHead — no changes to your existing backup software are required."
      }
    },
    {
      "@type": "Question",
      "name": "What is the minimum ExaGrid deployment size?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "ExaGrid starts with the EX10 entry-level appliance at 10TB full backup capacity (8×4TB Enterprise SAS drives, 16 GiB RAM, 4-core CPU, 2U chassis), suitable for SMBs and branch offices. The platform scales linearly — add appliances as data grows, with up to 32 appliances per system supporting 6TB to 2.7PB full backup."
      }
    },
    {
      "@type": "Question",
      "name": "Is ExaGrid available in Malaysia with local support?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. SE Distribution Sdn Bhd is the authorized ExaGrid distributor in Malaysia, based in Kuala Lumpur. We provide full pre-sales sizing, procurement, deployment, integration with your backup software, and ongoing post-sales support — no overseas escalation required."
      }
    },
    {
      "@type": "Question",
      "name": "How does ExaGrid compare to tape backup?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "ExaGrid eliminates the operational complexity of tape — no media management, no offsite transport, no restore delays. With adaptive deduplication averaging 20:1 (up to 50:1), ExaGrid delivers 50% lower total system cost vs competing solutions over time. Recovery is instant from the landing zone vs hours or days with tape."
      }
    }
  ]
};

const painPoints = [
  {
    icon: "ri-virus-line",
    title: "Ransomware Encrypted Your Backups",
    desc: "Traditional backup targets are network-accessible — ransomware operators know this and specifically target backup repositories to prevent recovery. 93% of ransomware attacks now target backup data.",
    stat: "93% of attacks target backups",
  },
  {
    icon: "ri-time-line",
    title: "Recovery Takes Hours or Days",
    desc: "Deduplication appliances that rehydrate data on restore force you to wait hours before VMs come back online. Every hour of downtime costs Malaysian enterprises an average of RM 180,000.",
    stat: "Avg RM 180K/hr downtime cost",
  },
  {
    icon: "ri-database-2-line",
    title: "Backup Windows Are Too Long",
    desc: "As data volumes grow, backup windows expand — often running into production hours. Traditional backup storage cannot keep up with modern data growth rates.",
    stat: "Data growing 40% year-on-year",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "Tape & Cloud Costs Keep Rising",
    desc: "Tape requires media management, offsite transport, and restore delays. Cloud backup costs balloon as data volumes grow. Neither delivers the instant recovery enterprises need.",
    stat: "3x higher TCO vs ExaGrid",
  },
];

const whyExaGrid = [
  {
    icon: "ri-shield-keyhole-line",
    title: "Tiered Air Gap — Ransomware Cannot Touch It",
    desc: "ExaGrid's non-network-facing repository stores backup data in a separate zone that ransomware cannot reach. Even if your entire production environment is encrypted, your backups remain intact and recoverable.",
    highlight: "Non-network-facing repository",
  },
  {
    icon: "ri-speed-line",
    title: "Landing Zone — Instant VM Recovery",
    desc: "Recent backups are kept in full, undeduplicated form in the landing zone. No rehydration wait. VMs come back online in minutes, not hours — dramatically reducing your RTO.",
    highlight: "Sub-15 minute VM recovery",
  },
  {
    icon: "ri-add-circle-line",
    title: "Scale-Out — No Forklift Upgrades",
    desc: "Add appliances as data grows. Each new node adds both capacity and performance linearly. No rip-and-replace, no downtime, no performance degradation as you scale. Supports up to 32 appliances per system.",
    highlight: "Up to 32 appliances per system",
  },
  {
    icon: "ri-lock-line",
    title: "Retention Time-Lock — Immutable Backups",
    desc: "Retention time-lock prevents any user — including administrators — from deleting or modifying backup data for a defined period. Ransomware operators cannot override it.",
    highlight: "Admin-proof immutability",
  },
  {
    icon: "ri-archive-line",
    title: "10–50x Deduplication — Massive Cost Savings",
    desc: "Adaptive deduplication runs post-process — never slowing your backup window. Average 20:1 data reduction (up to 50:1) dramatically cuts storage costs. Customers save 50% on total system cost vs competing solutions.",
    highlight: "Avg 20:1 dedup — 50% lower TCO vs competitors",
  },
  {
    icon: "ri-plug-line",
    title: "Works with 25+ Backup Applications",
    desc: "ExaGrid integrates natively with Veeam, Commvault, Veritas NetBackup, IBM Spectrum Protect, Dell EMC NetWorker, Arcserve, Oracle RMAN, Zerto, and many more. No rip-and-replace of your backup software.",
    highlight: "25+ backup app integrations",
  },
];

const comparisonRows = [
  { feature: "Ransomware Protection", exagrid: "Non-network-facing repo + immutable objects + time-lock", tape: "Offsite only — slow to restore", cloud: "Network-accessible — can be encrypted" },
  { feature: "VM Recovery Time", exagrid: "Sub-15 minutes (landing zone)", tape: "Hours to days (restore + rehydrate)", cloud: "Hours (download + rehydrate)" },
  { feature: "Backup Window Impact", exagrid: "None — post-process dedup", tape: "Slow write speeds", cloud: "Bandwidth-limited" },
  { feature: "Data Deduplication", exagrid: "Avg 20:1 dedup (up to 50:1) — post-process, never slows backup window", tape: "None", cloud: "Variable (provider-dependent)" },
  { feature: "Scale-Out", exagrid: "Linear — add nodes anytime", tape: "Add drives/libraries", cloud: "Unlimited (but costly)" },
  { feature: "Operational Complexity", exagrid: "Low — single management console", tape: "High — media management, offsite logistics", cloud: "Medium — egress costs, latency" },
  { feature: "3-Year TCO", exagrid: "50% lower total system cost vs competing solutions", tape: "Medium (media + labour + offsite logistics)", cloud: "Highest at scale — egress + storage fees" },
  { feature: "Local Support (Malaysia)", exagrid: "Full local support via SE Distribution", tape: "Varies by vendor", cloud: "Remote only" },
];

const useCases = [
  {
    icon: "ri-bank-line",
    title: "Financial Services",
    desc: "Banks and insurance firms use ExaGrid to meet BNM and SC data retention requirements with immutable backups — and recover from ransomware in minutes, not days.",
    stat: "Sub-15 min recovery",
  },
  {
    icon: "ri-hospital-line",
    title: "Healthcare",
    desc: "Hospitals protect PACS imaging, EMR, and clinical data with ExaGrid's ransomware-proof architecture — ensuring patient care continuity even after a cyberattack.",
    stat: "100% backup integrity",
  },
  {
    icon: "ri-government-line",
    title: "Government & GLC",
    desc: "Government agencies and GLCs rely on ExaGrid for compliant, long-term data retention with immutable storage that meets public sector data governance requirements.",
    stat: "Full audit trail compliance",
  },
  {
    icon: "ri-building-2-line",
    title: "Enterprise IT",
    desc: "Large enterprises with multi-petabyte backup environments use ExaGrid's scale-out architecture to grow without disruption — adding appliances as data volumes increase.",
    stat: "Up to 2.7PB per system",
  },
];

const faqs = [
  {
    q: "What makes ExaGrid ransomware-proof?",
    a: "ExaGrid uses a non-network-facing repository — the Tiered Air Gap — where backup data is stored in a separate zone that is not accessible from the network. Combined with immutable objects and retention time-lock, ransomware cannot encrypt, delete, or modify backup data even if the production environment is fully compromised.",
  },
  {
    q: "How fast is ExaGrid VM recovery?",
    a: "ExaGrid's landing zone keeps the most recent backups in full, undeduplicated form — meaning VMs can be recovered instantly without waiting for rehydration. Most organizations achieve sub-15 minute VM recovery times, compared to hours with traditional deduplication appliances.",
  },
  {
    q: "Does ExaGrid work with Veeam?",
    a: "Yes. ExaGrid integrates natively with Veeam Backup & Replication as a backup storage target. It also supports over 25 backup applications including Commvault, Veritas NetBackup, Veritas Backup Exec, IBM Spectrum Protect (TSM), Dell EMC NetWorker, Arcserve Backup/D2D/UDP, Micro Focus Data Protector, Quest NetVault, StorageCraft ShadowProtect, Idera SQL Safe, Oracle RMAN, Zerto Offsite Backup, HYCU, and BridgeHead — no changes to your existing backup software are required.",
  },
  {
    q: "What is the minimum ExaGrid deployment size?",
    a: "ExaGrid starts with the EX10 entry-level appliance at 10TB full backup capacity (8×4TB Enterprise SAS drives, 16 GiB RAM, 4-core CPU, 2U chassis), suitable for SMBs and branch offices. The platform scales linearly — add appliances as data grows, with up to 32 appliances per system supporting 6TB to 2.7PB full backup.",
  },
  {
    q: "Is ExaGrid available in Malaysia with local support?",
    a: "Yes. SE Distribution Sdn Bhd is the authorized ExaGrid distributor in Malaysia, based in Kuala Lumpur. We provide full pre-sales sizing, procurement, deployment, integration with your backup software, and ongoing post-sales support — no overseas escalation required.",
  },
  {
    q: "How does ExaGrid compare to tape backup?",
    a: "ExaGrid eliminates the operational complexity of tape — no media management, no offsite transport, no restore delays. With adaptive deduplication averaging 20:1 (up to 50:1), ExaGrid delivers 50% lower total system cost vs competing solutions over time. Recovery is instant from the landing zone vs hours or days with tape.",
  },
];

export default function ExaGridBackupPage() {
  useSEO({
    title: "ExaGrid Backup Malaysia | Ransomware-Proof Backup Appliance | SE Distribution",
    description: "ExaGrid backup appliances Malaysia — non-network-facing ransomware protection, instant VM recovery, 10–50x deduplication, scale-out to 2.7PB. Authorized ExaGrid distributor in Kuala Lumpur. Works with Veeam, Commvault, Veritas.",
    canonical: "https://www.sedistribution.com.my/exagrid-backup-malaysia",
    keywords: "ExaGrid backup Malaysia, ransomware-proof backup Malaysia, backup appliance Malaysia, ExaGrid distributor Malaysia, Veeam backup Malaysia, instant VM recovery Malaysia, enterprise backup Malaysia",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sedistribution.com.my/" },
          { "@type": "ListItem", "position": 2, "name": "Solutions", "item": "https://www.sedistribution.com.my/solutions" },
          { "@type": "ListItem", "position": 3, "name": "Enterprise Backup & Recovery", "item": "https://www.sedistribution.com.my/solutions/data-protection" },
          { "@type": "ListItem", "position": 4, "name": "ExaGrid Backup Malaysia", "item": "https://www.sedistribution.com.my/exagrid-backup-malaysia" }
        ]
      }
    ],
  });

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    setFormStatus("submitting");
    const data = new URLSearchParams(new FormData(formRef.current) as unknown as Record<string, string>);
    try {
      const res = await fetch("https://readdy.ai/api/form/d7iuir2n70uc8j62n2f0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        formRef.current.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />
      <StickyQuoteButton formId="exagrid-quote-form" label="Get a Sizing Quote" />

      <main className="pt-20">

        {/* Hero */}
        <section className="relative bg-[#1a1a1a] py-28 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=enterprise%20data%20center%20server%20room%20dark%20dramatic%20lighting%20rows%20of%20rack%20servers%20glowing%20LED%20lights%20professional%20IT%20infrastructure%2C%20cinematic%20wide%20angle%20shot%2C%20high%20detail%2C%20moody%20atmosphere%2C%20deep%20shadows%20and%20highlights%2C%20modern%20technology&width=1400&height=750&seq=egb-hero1&orientation=landscape"
            alt="ExaGrid Backup Malaysia"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/95 via-[#1a1a1a]/70 to-[#1a1a1a]/30"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/332e9681-9df1-4dcd-93f2-cf1098c6260f_exagrid-logo.png?v=ddd934934df6f2e00eabba5539238300"
                  alt="ExaGrid"
                  className="h-8 w-auto brightness-0 invert"
                />
                <span className="text-white/40 text-sm">|</span>
                <span className="text-xs font-semibold tracking-widest uppercase text-white/50">Authorized Distributor Malaysia</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 rounded-full px-4 py-1.5 mb-5 w-fit">
                <i className="ri-shield-keyhole-line text-red-400 text-xs"></i>
                <span className="text-xs font-semibold text-red-300 uppercase tracking-widest">Ransomware-Proof Backup</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
                The Only Backup That <strong>Ransomware Cannot Touch</strong>
              </h1>
              <p className="text-white/65 text-base leading-relaxed mb-6">
                ExaGrid is Malaysia's leading ransomware-proof backup appliance — with a non-network-facing repository that keeps your backups safe even when production is fully encrypted. Instant VM recovery. 10–50x deduplication. True scale-out architecture.
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">Non-Network-Facing Repo</span>
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">Sub-15 Min VM Recovery</span>
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">10–50x Deduplication</span>
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">Works with Veeam</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#exagrid-quote-form"
                  className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  Get a Sizing Assessment
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="tel:+60364127917"
                  className="border border-white/40 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-phone-line"></i>
                  +603 6412 7917
                </a>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <Link
                  to="/exagrid-spotlight"
                  className="inline-flex items-center gap-3 bg-white/5 border border-white/15 rounded-xl px-5 py-3.5 hover:bg-white/10 hover:border-white/25 transition-all cursor-pointer group w-fit"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                    <i className="ri-hard-drive-2-line text-white text-sm"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-white leading-tight">Want full product specs first?</p>
                    <p className="text-xs text-white/45">View ExaGrid Appliance Lineup — EX10 to EX84</p>
                  </div>
                  <i className="ri-arrow-right-line text-white/40 group-hover:text-white/70 transition-colors ml-1"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#1a1a1a] py-8 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "93%", label: "of ransomware attacks target backups" },
              { value: "&lt;15 min", label: "VM recovery from landing zone" },
              { value: "Avg 20:1", label: "dedup ratio (up to 50:1)" },
              { value: "2.7PB", label: "max effective capacity per system" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-white mb-1" dangerouslySetInnerHTML={{ __html: s.value }}></p>
                <p className="text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">The Problem</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">Why Traditional Backup Fails Against Ransomware</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">Modern ransomware operators specifically target backup repositories. If your backups are network-accessible, they are not safe.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {painPoints.map((p) => (
                <div key={p.title} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-500/20 rounded-lg mb-4">
                    <i className={`${p.icon} text-red-500 text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-[#1a1a1a] text-sm mb-2">{p.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">{p.desc}</p>
                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-xs font-bold text-red-500">{p.stat}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How ExaGrid Solves It */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">The ExaGrid Solution</span>
            <h2 className="text-3xl font-bold text-[#1a1a1a]">How ExaGrid Protects Your Backups</h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">ExaGrid is purpose-built to solve the three biggest backup challenges: ransomware, recovery speed, and scale — with a unique architecture found nowhere else.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyExaGrid.map((w) => (
              <div key={w.title} className="bg-[#f9f7f4] rounded-lg p-6 border border-transparent hover:border-[#1a1a1a]/10 transition-colors">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] rounded-lg mb-4">
                  <i className={`${w.icon} text-white text-xl`}></i>
                </div>
                <div className="inline-block bg-[#1a1a1a]/5 text-[#1a1a1a] text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mb-3">{w.highlight}</div>
                <h3 className="font-semibold text-[#1a1a1a] text-sm mb-2">{w.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Architecture Diagram */}
        <section className="py-20 bg-[#1a1a1a]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-3 block">Architecture</span>
              <h2 className="text-3xl font-bold text-white">The ExaGrid Tiered Air Gap Architecture</h2>
              <p className="text-white/55 text-sm mt-3 max-w-xl mx-auto">Two zones — one for speed, one for safety. The landing zone delivers instant recovery. The repository zone is completely isolated from the network.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                {[
                  {
                    zone: "Landing Zone",
                    color: "bg-green-500/20 border-green-500/30",
                    textColor: "text-green-400",
                    icon: "ri-speed-line",
                    title: "Recent Backups — Full & Undeduplicated",
                    points: [
                      "Stores the most recent backup data in full, undeduplicated form",
                      "Enables instant VM recovery — no rehydration wait",
                      "Network-accessible for backup software to write to",
                      "Automatically moves older data to the repository zone",
                    ],
                  },
                  {
                    zone: "Repository Zone",
                    color: "bg-red-500/20 border-red-500/30",
                    textColor: "text-red-400",
                    icon: "ri-shield-keyhole-line",
                    title: "Older Backups — Deduplicated & Isolated",
                    points: [
                      "Non-network-facing — ransomware cannot reach this zone",
                      "Immutable objects — data cannot be modified or deleted",
                      "Retention time-lock — admin-proof protection for defined period",
                      "10–50x deduplication reduces storage footprint dramatically",
                    ],
                  },
                ].map((zone) => (
                  <div key={zone.zone} className={`rounded-xl border p-6 ${zone.color}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                        <i className={`${zone.icon} ${zone.textColor} text-lg`}></i>
                      </div>
                      <div>
                        <p className={`text-xs font-bold uppercase tracking-widest ${zone.textColor}`}>{zone.zone}</p>
                        <p className="text-white text-sm font-semibold">{zone.title}</p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {zone.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-2 text-xs text-white/70">
                          <i className="ri-check-line text-white/50 mt-0.5 flex-shrink-0"></i>
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="w-full h-80 lg:h-full min-h-[400px] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=enterprise%20backup%20storage%20server%20appliance%20rack%20unit%20data%20center%20professional%20IT%20infrastructure%2C%20dark%20dramatic%20lighting%2C%20server%20room%20environment%2C%20glowing%20LED%20indicators%2C%20high%20detail%2C%20cinematic%20photography%2C%20modern%20technology%20hardware&width=700&height=600&seq=egb-arch1&orientation=portrait"
                  alt="ExaGrid Architecture"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Head-to-Head</span>
            <h2 className="text-3xl font-bold text-[#1a1a1a]">ExaGrid vs Tape vs Cloud Backup</h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">See how ExaGrid compares to traditional tape and cloud backup across the metrics that matter most to IT managers.</p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1a1a1a]">
                  <th className="text-left text-xs font-bold text-white/60 uppercase tracking-wider px-6 py-4 w-1/4">Feature</th>
                  <th className="text-center text-xs font-bold text-white uppercase tracking-wider px-6 py-4 w-1/4">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-checkbox-circle-fill text-green-400"></i>
                      ExaGrid
                    </span>
                  </th>
                  <th className="text-center text-xs font-bold text-white/60 uppercase tracking-wider px-6 py-4 w-1/4">Tape Backup</th>
                  <th className="text-center text-xs font-bold text-white/60 uppercase tracking-wider px-6 py-4 w-1/4">Cloud Backup</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {comparisonRows.map((row, i) => (
                  <tr key={row.feature} className={i % 2 === 0 ? "bg-white" : "bg-[#fafaf9]"}>
                    <td className="px-6 py-4 text-xs font-semibold text-[#1a1a1a]">{row.feature}</td>
                    <td className="px-6 py-4 text-xs text-green-700 font-medium text-center">{row.exagrid}</td>
                    <td className="px-6 py-4 text-xs text-gray-500 text-center">{row.tape}</td>
                    <td className="px-6 py-4 text-xs text-gray-500 text-center">{row.cloud}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Who Uses ExaGrid</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">ExaGrid Backup in Malaysia — Industry Use Cases</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {useCases.map((u) => (
                <div key={u.title} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                    <i className={`${u.icon} text-[#1a1a1a] text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-[#1a1a1a] text-sm mb-2">{u.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">{u.desc}</p>
                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-xs font-bold text-[#1a1a1a]">{u.stat}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integrations */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="w-full lg:w-1/2 h-72 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=IT%20administrator%20managing%20backup%20software%20dashboard%20multiple%20monitors%20server%20room%20enterprise%20data%20management%20system%2C%20professional%20environment%2C%20dramatic%20lighting%2C%20photorealistic%2C%20dark%20moody%20atmosphere&width=700&height=450&seq=egb-int1&orientation=landscape"
                alt="ExaGrid Integrations"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="w-full lg:w-1/2">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Ecosystem Compatibility</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-5">Works with Your Existing Backup Software</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                ExaGrid integrates natively with all major backup software vendors — no rip-and-replace of your existing backup infrastructure. Simply add ExaGrid as your backup storage target and immediately benefit from faster backups, instant recovery, and ransomware protection.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-7">
                {[
                  "Veeam Backup & Replication",
                  "Commvault",
                  "Veritas NetBackup",
                  "Veritas Backup Exec",
                  "IBM Spectrum Protect (TSM)",
                  "Dell EMC NetWorker",
                  "Arcserve Backup, D2D & UDP",
                  "Micro Focus Data Protector",
                  "Quest NetVault & vRanger",
                  "StorageCraft ShadowProtect",
                  "Bacula Systems",
                  "Idera SQL Safe",
                  "Oracle RMAN",
                  "Zerto Offsite Backup",
                  "HYCU",
                  "BridgeHead",
                ].map((int) => (
                  <div key={int} className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-check-line text-[#1a1a1a] flex-shrink-0"></i>
                    {int}
                  </div>
                ))}
              </div>
              <Link
                to="/exagrid-spotlight"
                className="inline-flex items-center gap-2 border border-[#1a1a1a] text-[#1a1a1a] text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#1a1a1a] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                View Full ExaGrid Product Lineup
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section id="exagrid-quote-form" className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row gap-12 items-start">
              {/* Left — Trust Points */}
              <div className="w-full lg:w-2/5">
                <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Get Started</span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-4">Get a Free ExaGrid Sizing Assessment</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">
                  Tell us about your backup environment and we'll size the right ExaGrid appliance for your data volume, retention requirements, and budget — with a full TCO comparison vs your current solution.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: "ri-ruler-line", title: "Right-Sized for Your Environment", desc: "We size ExaGrid based on your actual backup data volume, change rate, and retention policy — not generic estimates." },
                    { icon: "ri-money-dollar-circle-line", title: "Full TCO Comparison", desc: "We'll show you exactly how ExaGrid compares to your current backup costs over 3 years — including deduplication savings." },
                    { icon: "ri-customer-service-2-line", title: "Local KL-Based Support", desc: "Our team is based in Kuala Lumpur — no overseas escalation, no time zone delays, no language barriers." },
                    { icon: "ri-time-line", title: "Response Within 1 Business Day", desc: "We respond to all sizing requests within 1 business day with a sizing recommendation and TCO comparison." },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] rounded-lg flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-white text-base`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1a1a1a] mb-0.5">{item.title}</p>
                        <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — Form */}
              <div className="w-full lg:w-3/5">
                {formStatus === "success" ? (
                  <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-4">
                      <i className="ri-checkbox-circle-line text-green-500 text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">Assessment Request Received</h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-6">
                      Thank you! Our ExaGrid specialist will review your environment details and get back to you within 1 business day with a sizing recommendation and TCO comparison.
                    </p>
                    <button
                      onClick={() => setFormStatus("idle")}
                      className="text-sm font-medium text-[#1a1a1a] underline cursor-pointer"
                    >
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <form
                    ref={formRef}
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="bg-white rounded-xl border border-gray-100 p-8 space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="Ahmad bin Razak"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Company Name *</label>
                        <input
                          type="text"
                          name="company"
                          required
                          placeholder="Your Company Sdn Bhd"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Work Email *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="you@company.com"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="+60 12 345 6789"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Current Backup Data Volume</label>
                        <select
                          name="backup_volume"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-[#1a1a1a] outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select range</option>
                          <option value="under-10tb">Under 10TB</option>
                          <option value="10-50tb">10TB – 50TB</option>
                          <option value="50-200tb">50TB – 200TB</option>
                          <option value="200-500tb">200TB – 500TB</option>
                          <option value="500tb-plus">500TB+</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Current Backup Software</label>
                        <select
                          name="backup_software"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-[#1a1a1a] outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select software</option>
                          <option value="veeam">Veeam Backup & Replication</option>
                          <option value="commvault">Commvault</option>
                          <option value="veritas">Veritas NetBackup</option>
                          <option value="ibm">IBM Spectrum Protect</option>
                          <option value="cohesity">Cohesity</option>
                          <option value="rubrik">Rubrik</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Tell Us About Your Environment</label>
                      <textarea
                        name="environment_details"
                        rows={3}
                        maxLength={500}
                        placeholder="Number of VMs, backup frequency, retention requirements, any ransomware concerns, current pain points..."
                        className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors resize-none"
                      ></textarea>
                    </div>
                    {formStatus === "error" && (
                      <p className="text-xs text-red-500">Something went wrong. Please try again or call us directly.</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === "submitting"}
                      className="w-full bg-[#1a1a1a] text-white text-sm font-semibold py-3.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {formStatus === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Get My Free Sizing Assessment
                          <i className="ri-arrow-right-line"></i>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-400 text-center">We respond within 1 business day. No spam, ever.</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">FAQ</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">ExaGrid Backup Malaysia — Common Questions</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-gray-100 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-[#fafaf9] transition-colors"
                  >
                    <span className="text-sm font-semibold text-[#1a1a1a] pr-4">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${openFaq === i ? "ri-subtract-line" : "ri-add-line"} text-gray-400 text-base`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-[#1a1a1a] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20data%20center%20server%20room%20professional%20enterprise%20technology%20infrastructure%2C%20dramatic%20lighting%2C%20dark%20atmosphere%2C%20wide%20shot%2C%20cinematic%20photography%2C%20high%20detail%2C%20rows%20of%20servers%20glowing&width=1400&height=500&seq=egb-cta1&orientation=landscape"
            alt="Deploy ExaGrid"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/80 to-[#1a1a1a]/95"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Make Your Backups Ransomware-Proof?</h2>
            <p className="text-white/60 text-sm max-w-lg mx-auto mb-8 leading-relaxed">
              As Malaysia's authorized ExaGrid distributor, SE Distribution handles everything from sizing and procurement to deployment, integration, and ongoing support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#exagrid-quote-form"
                className="bg-white text-[#1a1a1a] text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get a Free Sizing Assessment
              </a>
              <Link
                to="/exagrid-spotlight"
                className="border border-white/40 text-white text-sm font-medium px-8 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-external-link-line"></i>
                View Full ExaGrid Lineup
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
